import { useEffect, useState } from "react";
import { TIME_ZONES } from "@krut/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authClient, unwrap } from "../api.ts";
import { useMe, useSettings } from "../hooks.ts";
import { browserZone } from "../lib/cron.ts";
import { Button, Card, ErrorText, Field, PageHeader, Select, Spinner } from "../components/ui.tsx";

type SettingsInput = {
  timezone?: string | null;
  toolUpdates?: boolean;
  briefing?: { muted?: boolean; times?: string[] | null; weeklyMuted?: boolean; weeklyDay?: number | null; weeklyTime?: string | null };
};

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (json: SettingsInput) => unwrap(api.me.settings.$put({ json })),
    onSuccess: (data) => {
      qc.setQueryData(["settings"], data);
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** "Asia/Kolkata (GMT+5:30)". */
function zoneLabel(zone: string): string {
  try {
    const off = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value;
    return off ? `${zone.replace(/_/g, " ")} (${off})` : zone;
  } catch {
    return zone;
  }
}


export function SettingsPage() {
  const settings = useSettings();
  const save = useSaveSettings();
  const local = browserZone();
  const [zone, setZone] = useState<string>("");
  // Follow the saved zone only when it changes (saving the other settings must not undo a pick).
  const savedZone = settings.data?.timezone;
  useEffect(() => {
    if (savedZone !== undefined) setZone(savedZone ?? local);
  }, [savedZone, local]);
  // Only zones the assistant knows (the server refuses others).
  const zones = TIME_ZONES;

  if (settings.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (settings.error) return <ErrorText error={settings.error} />;
  const saved = settings.data.timezone;
  const now = (z: string) => {
    try {
      return new Date().toLocaleTimeString(undefined, { timeZone: z, hour: "numeric", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  return (
    <>
      <PageHeader
        title="Settings"
        description="Your time zone and where you're signed in. Briefings are under Schedules & briefings; chat-app options under Connected apps → Chat apps."
      />

      <h2 className="mb-3 text-base font-semibold">Time zone</h2>
      <Card className="mb-8 space-y-4 p-5">
        <Field
          label="Your time zone"
          hint={<>Your assistant uses it for scheduled tasks (a task “every day at 9am” runs at 9am here), for “today” and “tomorrow”, and for the dates it writes. Saving restarts your assistant, which takes about a minute.</>}
        >
          <Select value={zone} onChange={(e) => setZone(e.target.value)} className="max-w-md">
            {zones.map((z) => <option key={z} value={z}>{zoneLabel(z)}</option>)}
          </Select>
        </Field>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Button variant="primary" disabled={save.isPending || !zone || zone === saved} onClick={() => save.mutate({ timezone: zone })}>
            {save.isPending ? "Saving…" : "Save time zone"}
          </Button>
          {zone !== local && (
            <button type="button" className="text-[var(--color-accent)] hover:underline" onClick={() => setZone(local)}>
              Use this browser's time zone ({local.replace(/_/g, " ")})
            </button>
          )}
          <span className="text-[var(--muted)]">
            {saved ? <>Saved: {saved.replace(/_/g, " ")}, now {now(saved)}.</> : <>Not set yet — your assistant is on UTC.</>}
          </span>
        </div>
      </Card>

      <ErrorText error={save.error} />

      <SessionsSection />
      <SignOut />
    </>
  );
}

/** Phones have no sidebar footer (where the sign-out button is), so it is here too. */
function SignOut() {
  const qc = useQueryClient();
  return (
    <Button
      className="md:hidden"
      onClick={async () => {
        await authClient.signOut();
        qc.clear();
        location.href = "/login";
      }}
    >
      Sign out
    </Button>
  );
}

/** "Chrome on Mac" from a user-agent string; good enough to recognise your own devices. */
export function deviceLabel(ua: string | null): string {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\/|Opera/.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "A browser";
  const os = /iPhone|iPad/.test(ua) ? "iPhone or iPad" : /Android/.test(ua) ? "Android" : /Mac OS X|Macintosh/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
}

const when = (d: string) => new Date(d).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

/** Where you're signed in, with a way to sign out of any device (a lost laptop, a shared computer). */
function SessionsSection() {
  const me = useMe();
  // Only the person themselves sees and ends their sign-ins, never an admin acting as them.
  return me.data && !me.data.user.impersonatedBy ? <SessionsList /> : null;
}

function SessionsList() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["sessions"], queryFn: () => unwrap(api.me.sessions.$get()) });
  const done = () => qc.invalidateQueries({ queryKey: ["sessions"] });
  const end = useMutation({ mutationFn: (id: string) => unwrap(api.me.sessions[":id"].$delete({ param: { id } })), onSuccess: done });
  const others = useMutation({ mutationFn: () => unwrap(api.me.sessions["sign-out-others"].$post()), onSuccess: done });
  const rows = list.data?.sessions ?? [];
  return (
    <>
      <h2 className="mb-3 mt-8 text-base font-semibold">Signed-in devices</h2>
      <Card className="mb-8 p-5">
        <p className="mb-4 text-sm text-[var(--muted)]">
          Where you're signed in. A device you haven't used for a week is signed out on its own, and every device has to sign in again after {list.data?.maxDays ?? 30} days.
        </p>
        {list.isPending ? (
          <Spinner />
        ) : (
          <ul className="divide-y divide-[var(--line)] text-sm">
            {rows.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <div>
                  <div className="font-medium">{deviceLabel(r.userAgent)} {r.current && <span className="ml-1 text-xs font-normal text-emerald-700">this device</span>}</div>
                  <div className="text-xs text-[var(--muted)]">Signed in {when(r.createdAt)} · last active {when(r.lastActiveAt)}</div>
                </div>
                {!r.current && (
                  <Button variant="ghost" className="h-8" disabled={end.isPending} onClick={() => end.mutate(r.id)}>Sign out</Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {rows.filter((r) => !r.current).length > 1 && (
          <Button className="mt-3" disabled={others.isPending} onClick={() => others.mutate()}>Sign out of all other devices</Button>
        )}
        <ErrorText error={list.error ?? end.error ?? others.error} />
      </Card>
    </>
  );
}
